/* eslint-disable unused-imports/no-unused-vars */
import { ChangeEvent, FC } from 'react';
import { DescriptionList } from '@doda/io-react/common';
import { SEARCH_CONDITION_ITEM_TITLE } from '@doda/common/src/constants/screenStructure/searchConditionItems';
import {
  EmployNumRequestCode,
  EmployeeAverageAgeRequestCode,
  EstablishRequestCode,
  HolidayAndWayOfWorkingRequestCode,
  JobOfferKindRequestCode,
  LanguageSkillsRequestCode,
  PositionsRequestCode,
  RequirementsRequestCode,
  WelfareBenefitsRequestCode,
  WorkplaceEnvironmentRequestCode,
} from '@doda/api-client/aspida/@types';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import {
  EmployNumListType,
  EmployeeAverageAgeListType,
  EstablishListType,
  HolidayAndWayOfWorkingListType,
  JobOfferKindListType,
  LanguageSkillListType,
  PositionListType,
  RequirementListType,
  WelfareBenefitsListType,
  WorkingPlaceEnvironment,
} from 'types/kodawari';
import { SelectiveSettingFormItem } from './components/SelectiveSettingFormItem';
import styles from './index.module.scss';

// Note:  ConditionChangeページに影響が出るので、不必要なpropsは一時的にanyで回避
/**
 * SearchSelectiveSettingFormのプロパティを定義
 */
interface Props {
  holidayAndWayOfWorking: {
    holidayAndWayOfWorkingList: HolidayAndWayOfWorkingListType[];
    updateIsCheckedHolidayAndWayOfWorkingList: (
      event: ChangeEvent<HTMLInputElement>
    ) => HolidayAndWayOfWorkingRequestCode[];
  };
  requirements: {
    requirementList: RequirementListType[];
    updateIsCheckedRequirementList: (
      event: ChangeEvent<HTMLInputElement>
    ) => RequirementsRequestCode[];
  };
  workplaceEnvironment: {
    WorkplaceEnvironmentList: WorkingPlaceEnvironment[];
    updateIsCheckedWorkplaceEnvironmentList: (
      event: ChangeEvent<HTMLInputElement>
    ) => WorkplaceEnvironmentRequestCode[];
  };
  welfareBenefits: {
    welfareBenefitsList: WelfareBenefitsListType[];
    updateIsCheckedWelfareBenefitsList: (
      event: ChangeEvent<HTMLInputElement>
    ) => WelfareBenefitsRequestCode[];
  };
  positions: {
    positionList: PositionListType[];
    updateIsCheckedPositionList: (
      event: ChangeEvent<HTMLInputElement>
    ) => PositionsRequestCode[];
  };
  languageSkill: {
    languageSkillList: LanguageSkillListType[];
    updateIsCheckedLanguageSkillList: (
      event: ChangeEvent<HTMLInputElement>
    ) => LanguageSkillsRequestCode[];
  };
  employeeAverageAge: {
    employeeAverageAgeList: EmployeeAverageAgeListType[];
    updateIsCheckedEmployeeAverageAgeList: (
      event: ChangeEvent<HTMLInputElement>
    ) => EmployeeAverageAgeRequestCode[];
  };
  employNum: {
    employNumList: EmployNumListType[];
    updateIsCheckedEmployNumList: (
      event: ChangeEvent<HTMLInputElement>
    ) => EmployNumRequestCode[];
  };
  establish: {
    establishList: EstablishListType[];
    updateIsCheckedEstablishList: (
      event: ChangeEvent<HTMLInputElement>
    ) => EstablishRequestCode[];
  };
  jobOfferKind: {
    jobOfferKindList: JobOfferKindListType[];
    updateIsCheckedJobOfferKindList: (
      event: ChangeEvent<HTMLInputElement>
    ) => JobOfferKindRequestCode | undefined;
  };
  updateJobCount: (currentCondition?: JobCountQuery) => Promise<void>;
}

/**
 * こだわり条件でさらに絞るフォームを表示するコンポーネント
 *
 * @param {Props} props - フォームのプロパティ
 * @returns {JSX.Element} - こだわり条件でさらに絞るフォームの要素
 */
export const SearchSelectiveSettingForm: FC<Props> = ({
  holidayAndWayOfWorking,
  requirements,
  workplaceEnvironment,
  welfareBenefits,
  positions,
  languageSkill,
  employeeAverageAge,
  employNum,
  establish,
  jobOfferKind,
  updateJobCount,
}) => {
  return (
    <>
      <h3 className={styles.searchSelectiveSettingForm__title}>
        こだわり条件でさらに絞る
      </h3>
      <div className={styles.searchSelectiveSettingForm__wrapper}>
        <DescriptionList
          type="column2"
          column2dataBgColor="gray"
          listData={[
            {
              title: SEARCH_CONDITION_ITEM_TITLE.WORKSTYLE,
              contents: (
                <SelectiveSettingFormItem
                  list={holidayAndWayOfWorking.holidayAndWayOfWorkingList}
                  type="holidayAndWayOfWorking"
                  updateIsCheckBox={
                    holidayAndWayOfWorking.updateIsCheckedHolidayAndWayOfWorkingList
                  }
                  updateJobCount={updateJobCount}
                />
              ),
            },
            {
              title: SEARCH_CONDITION_ITEM_TITLE.REQUIREMENT,
              contents: (
                <SelectiveSettingFormItem
                  list={requirements.requirementList}
                  type="requirements"
                  updateIsCheckBox={requirements.updateIsCheckedRequirementList}
                  updateJobCount={updateJobCount}
                />
              ),
            },
            {
              title: SEARCH_CONDITION_ITEM_TITLE.WORKPLACE_ENVIRONMENT,
              contents: (
                <SelectiveSettingFormItem
                  list={workplaceEnvironment.WorkplaceEnvironmentList}
                  type="workplaceEnvironment"
                  updateIsCheckBox={
                    workplaceEnvironment.updateIsCheckedWorkplaceEnvironmentList
                  }
                  updateJobCount={updateJobCount}
                />
              ),
            },
            {
              title: SEARCH_CONDITION_ITEM_TITLE.BENEFIT,
              contents: (
                <SelectiveSettingFormItem
                  list={welfareBenefits.welfareBenefitsList}
                  type="welfareBenefits"
                  updateIsCheckBox={
                    welfareBenefits.updateIsCheckedWelfareBenefitsList
                  }
                  updateJobCount={updateJobCount}
                />
              ),
            },
            {
              title: SEARCH_CONDITION_ITEM_TITLE.LANGUAGE,
              contents: (
                <SelectiveSettingFormItem
                  list={languageSkill.languageSkillList}
                  type="languageSkills"
                  updateIsCheckBox={
                    languageSkill.updateIsCheckedLanguageSkillList
                  }
                  updateJobCount={updateJobCount}
                />
              ),
            },
            {
              title: SEARCH_CONDITION_ITEM_TITLE.POSITION,
              contents: (
                <SelectiveSettingFormItem
                  list={positions.positionList}
                  type="positions"
                  updateIsCheckBox={positions.updateIsCheckedPositionList}
                  updateJobCount={updateJobCount}
                />
              ),
            },
            {
              title: SEARCH_CONDITION_ITEM_TITLE.AVERAGE_AGE,
              contents: (
                <SelectiveSettingFormItem
                  list={employeeAverageAge.employeeAverageAgeList}
                  type="employeeAverageAge"
                  updateIsCheckBox={
                    employeeAverageAge.updateIsCheckedEmployeeAverageAgeList
                  }
                  updateJobCount={updateJobCount}
                />
              ),
            },
            {
              title: SEARCH_CONDITION_ITEM_TITLE.NUMBER_OF_EMPLOYEES,
              contents: (
                <SelectiveSettingFormItem
                  list={employNum.employNumList}
                  type="employNum"
                  updateIsCheckBox={employNum.updateIsCheckedEmployNumList}
                  updateJobCount={updateJobCount}
                />
              ),
            },
            {
              title: SEARCH_CONDITION_ITEM_TITLE.ESTABLISHMENT,
              contents: (
                <SelectiveSettingFormItem
                  list={establish.establishList}
                  type="establish"
                  updateIsCheckBox={establish.updateIsCheckedEstablishList}
                  updateJobCount={updateJobCount}
                />
              ),
            },
            {
              title: SEARCH_CONDITION_ITEM_TITLE.APPLICATION_METHOD,
              contents: (
                <SelectiveSettingFormItem
                  list={jobOfferKind.jobOfferKindList}
                  type="jobOfferKind"
                  updateIsCheckBox={
                    jobOfferKind.updateIsCheckedJobOfferKindList
                  }
                  updateJobCount={updateJobCount}
                  propsClassName="searchSelectiveSettingForm__jobOfferKind"
                />
              ),
            },
          ]}
        />
      </div>
    </>
  );
};
